import {FormEventHandler, useCallback, useContext, useEffect, useMemo, useRef} from 'react';
import {CONTENT_EDIT_DEBOUNCE} from '../../util/constants';
import {ContentMode} from '../../util/enums';
import {AppContext} from '../context/appContext';
import {ContentContext} from '../context/contentContext';

export interface EditableComponentProps {
    textKey: string;
    className?: string;
}

export function useEditableComponent<T extends HTMLElement>(textKey: string): [string, FormEventHandler, boolean] {
    const {user} = useContext(AppContext).userContext;
    const {onUpdate, content, contentMode, language} = useContext(ContentContext);
    const updateDebounce = useRef<number | undefined>(undefined);
    const text = useMemo(() => {
        return content[textKey]?.content ?? '';
    }, [content, textKey]);
    const editable = useMemo(() => {
        return !!user && contentMode === ContentMode.PREVIEW;
    }, [user, contentMode]);
    const onChange = useCallback((evt: React.FormEvent<T>) => {
        clearTimeout();
        let value = evt.currentTarget.innerHTML;
        updateDebounce.current = window.setTimeout(() => {
            const divStart = /<div>/g;
            const divEnd = /<\/div>/g;
            const br = /<br\/?>/g;
            value = value.replace(divStart, '\n').replace(divEnd, '').replace(br, '');
            onUpdate(textKey, value, language);
        }, CONTENT_EDIT_DEBOUNCE);
    }, [language]);

    function clearTimeout() {
        window.clearTimeout(updateDebounce.current);
        updateDebounce.current = undefined;
    }

    useEffect(() => {
        return function() {
            clearTimeout();
        };
    }, []);

    return [text, onChange, editable];
}